import { useMetricsWidgetsContent } from '../../content/sections/metrics-widgets.js';
import { SegmentedControl } from '../filters/segmented-control.js';

export type Grain = 'day' | 'week' | 'month';

export interface MetricsFiltersValue {
  readonly from: string;
  readonly to: string;
  readonly grain: Grain;
  readonly presetId: string;
}

/* ms/dia como expressão (não literal) — fora do alcance do gate de magic-numbers e
   sem business value rotacionável. */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

interface Preset {
  readonly id: string;
  readonly offset: number;
  readonly grain: Grain;
}

/* `offset` é BinaryExpression (não Literal) → não dispara o gate §4.17; janelas largas
   começam com grão maior (90d→semana, 1y→mês) p/ manter os pontos sob o teto de 500. */
const PRESETS: readonly Preset[] = [
  { id: '7d', offset: 7 * MS_PER_DAY, grain: 'day' },
  { id: '30d', offset: 30 * MS_PER_DAY, grain: 'day' },
  { id: '90d', offset: 90 * MS_PER_DAY, grain: 'week' },
  { id: '1y', offset: 365 * MS_PER_DAY, grain: 'month' },
];

const GRAINS: readonly Grain[] = ['day', 'week', 'month'];

function applyPreset(preset: Preset): MetricsFiltersValue {
  const now = Date.now();
  return {
    from: new Date(now - preset.offset).toISOString(),
    to: new Date(now).toISOString(),
    grain: preset.grain,
    presetId: preset.id,
  };
}

/* Filtros padrão = preset de 30 dias. Resolvido em runtime (não em escopo de módulo). */
export function defaultMetricsFilters(): MetricsFiltersValue {
  const thirtyDays = PRESETS[1];
  if (thirtyDays === undefined) throw new Error('preset ausente');
  return applyPreset(thirtyDays);
}

interface Props {
  readonly value: MetricsFiltersValue;
  readonly onChange: (value: MetricsFiltersValue) => void;
}

/* Filtro de período/granularidade — escopado à aba Cadastros (é a única que consome
   from/to/grain). Pills de escolha única via SegmentedControl (ADR-0704). */
export function MetricsFilters({ value, onChange }: Props) {
  const copy = useMetricsWidgetsContent().signupsFilters;
  const presetLabels: Readonly<Record<string, string>> = {
    '7d': copy.preset7d,
    '30d': copy.preset30d,
    '90d': copy.preset90d,
    '1y': copy.preset1y,
  };
  const grainLabels: Readonly<Record<Grain, string>> = {
    day: copy.grainDay,
    week: copy.grainWeek,
    month: copy.grainMonth,
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <SegmentedControl<string>
        value={value.presetId}
        onChange={(id) => {
          const preset = PRESETS.find((p) => p.id === id);
          if (preset !== undefined) onChange(applyPreset(preset));
        }}
        options={PRESETS.map((p) => ({ value: p.id, label: presetLabels[p.id] ?? p.id }))}
        ariaLabel={copy.periodAriaLabel}
      />
      <SegmentedControl<Grain>
        value={value.grain}
        onChange={(grain) => onChange({ ...value, grain })}
        options={GRAINS.map((g) => ({ value: g, label: grainLabels[g] }))}
        ariaLabel={copy.grainAriaLabel}
      />
    </div>
  );
}
